import { CCol } from "@coreui/react";
import {
  useEsgSurveyQuestions,
  useEsgSurveyResponsesStandalone,
} from "src/features/esg-survey/hooks";

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSteps } from "react-step-builder";
import { storage } from "src/adapters/common-set-up/firebase";
import zeroBeesLogo from "src/assets/images/ZeroBees_logo_new.png";
import zeroBeesWhiteLogo from "src/assets/images/logo-white.png";
import { LoadingSpinner } from "src/components";
import NoDataWarning from "src/components/NoDataWarning/NoDataWarning";
import BackButton from "src/components/StepContainer/components/BackButton";
import { selectOrgData, selectTrack } from "src/store/projectSelectors";
import {
  EsgInfoCollapse,
  PrintEsg,
  SurveyChart,
  SurveyTable,
} from "../components";
import SummaryCopy from "../components/summary-content/SummaryCopy";
import StyledDiv from "../style/Summary.style";
import { createEsgSummaryRows } from "../utils";

const checkHasFiles = async ({ orgId, area, questionKey }) => {
  try {
    const storagePath = `survey/standalone/${orgId}/${area}/${questionKey}`;
    const fileStorageRef = storage.ref().child(storagePath);
    const res = await fileStorageRef.listAll();

    return res?.items?.length > 0;
  } catch (e) {
    console.error("error checking files", e);
    return false;
  }
};

const SummaryEsgContent = ({ orgId, isStep }) => {
  const { currentSurvey, status: questionsStatus } = useEsgSurveyQuestions({
    orgId,
    isStandalone: true,
  });

  const surveyAreas = currentSurvey?.areas || [];

  const { prev } = useSteps();

  const track = useSelector(selectTrack);

  const {
    internalSurveyResponses,
    adminSurveyResponses,
    externalSurveyResponses,
    totalSurveyResponses,
    status: responsesStatus,
  } = useEsgSurveyResponsesStandalone({ orgId, isStandalone: true });

  const { orgName } = useSelector(selectOrgData) || {};

  const { rows, areas, finalPercentage, finalScore, totalPossible } =
    createEsgSummaryRows({
      surveyAreas,
      internalSurveyResponses,
      adminSurveyResponses,
      externalSurveyResponses,
      totalSurveyResponses,
    });

  const isVerified = adminSurveyResponses?.isVerified;

  const isFinal = adminSurveyResponses?.isFinal;

  const isLoading =
    questionsStatus === "loading" || responsesStatus === "loading";

  const [evidenceRate, setEvidenceRate] = useState(0);

  useEffect(() => {
    const checkEvidence = async () => {
      if (!orgId) return;
      if (!currentSurvey) return;

      const promises = [];

      currentSurvey?.areas?.forEach((area) => {
        area?.questions?.forEach((question) => {
          promises.push(
            checkHasFiles({ orgId, area: area.key, questionKey: question.key })
          );
        });
      });

      const evidence = await Promise.all(promises);

      const evidenceRate =
        Math.round((evidence.filter(Boolean).length / evidence.length) * 1e4) /
        1e2;

      setEvidenceRate(evidenceRate);
    };
    checkEvidence();
  }, [currentSurvey]);

  return (
    <StyledDiv className="d-flex flex-column align-items-center mt-4">
      {isStep && <BackButton onClick={prev} label={"Back"} />}
      <img
        src={zeroBeesWhiteLogo}
        id="white-logo"
        alt="zerobees logo"
        width="120px"
        className="mr-4 position-absolute"
      />
      <CCol
        className={`d-flex flex-column mb-4 w-100 align-items-center ${
          !isStep ? "mt-5" : ""
        }`}
      >
        <div>
          <img src={zeroBeesLogo} alt="" id="logo" width={100} />
        </div>
        <p
          style={{ fontSize: "72px", fontWeight: "bold", lineHeight: 1.2 }}
          className="text-info mb-0"
        >
          {orgName}
        </p>
        <h2
          className="text-primary font-weight-light"
          style={{ fontSize: "2rem" }}
        >
          Results
        </h2>
      </CCol>
      <SummaryCopy
        track={track}
        evidenceRate={evidenceRate}
        isVerified={isVerified}
      />
      <EsgInfoCollapse track={track} />

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <CCol className="mb-4 mt-4">
          <SurveyChart
            areas={areas}
            finalPercentage={finalPercentage}
            totalPossible={totalPossible}
            isFinal={isFinal}
          />
          <div className="d-flex flex-column align-items-center">
            <p style={{ fontSize: "20px", marginTop: "1rem" }}>
              The total number of points:{" "}
              <b>
                {finalScore} / {totalPossible}
              </b>{" "}
              possible
            </p>
            {orgName && (
              <PrintEsg
                finalPercentage={finalPercentage}
                finalScore={finalScore}
                orgName={orgName}
              />
            )}
          </div>
        </CCol>
      )}
      {!!totalSurveyResponses && !!surveyAreas?.length ? (
        <>
          {areas.map((area, idx) => (
            <div
              style={{
                overflow: "auto",
                maxWidth: "100%",
              }}
              key={idx}
              className={`mt-4 section-${idx}`}
            >
              <h3 className="text-left mb-3">{area.label}</h3>
              <SurveyTable
                addTableClasses={`area-${idx}`}
                rows={rows.filter((r) => r.area === area.label)}
              />
            </div>
          ))}

          <SurveyTable rows={rows} isPrint />
        </>
      ) : (
        <NoDataWarning
          className="mt-3 mx-auto"
          text={"No survey responses yet"}
        />
      )}
    </StyledDiv>
  );
};

export default SummaryEsgContent;
